import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { authService } from '@/services/authService';
import {
  AuthConfirmationMethod,
  AuthStatus,
  ConfirmAccountData,
  InitAccountAuthResponse,
  RequestConfirmAccount,
  RequestInitAccountAuth,
} from '@/types/auth';
import { requestContact } from '@tma.js/sdk-react';
import type { AuthContextValue } from './AuthContext.d';
import { authStep } from '@/services/authStepService';
import { paths } from '@/navigation/paths';
import { PhoneRequestError } from '@/errors/PhoneRequestError';
import { registerUnauthorizedHandler } from './authUnauthorized';
import { router } from '@/navigation/router';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING);
  const [phoneNumber, setPhoneNumber] = useState<number | null>(null);
  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [isUserConfirmed, setIsUserConfirmed] = useState<boolean | null>(null);
  const [confirmationMethod, setConfirmationMethodState] = useState<AuthConfirmationMethod | null>(
    null
  );
  const queryClient = useQueryClient();

  const { data: authData, isLoading: isCheckingAuth } = useQuery({
    queryKey: ['auth-status'],
    queryFn: () => authService.checkAuth(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isCheckingAuth && authData) {
      if (authData.isAuthenticated) {
        setStatus(AuthStatus.AUTHENTICATED);
      } else {
        setStatus(AuthStatus.PHONE_REQUEST);
      }
    } else if (!isCheckingAuth && !authData) {
      setStatus(AuthStatus.PHONE_REQUEST);
    }
  }, [authData, isCheckingAuth]);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setPhoneNumber(null);
      setOtpCode(null);
      setIsUserConfirmed(null);
      setConfirmationMethodState(null);
      setStatus(AuthStatus.UNAUTHENTICATED);
      authStep.clearAll();
      queueMicrotask(() => {
        void router.navigate(paths.auth.phone, { replace: true });
      });
    });
  }, []);

  const requestPhonePermission = async (): Promise<number> => {
    const contact = await requestContact();
    if (contact?.contact?.phone_number) {
      return Number(contact.contact.phone_number);
    }

    throw new PhoneRequestError();
  };

  const initAuthMutation = useMutation({
    mutationFn: (request: RequestInitAccountAuth) => authService.initAccountAuth(request),
  });

  const loginMutation = useMutation({
    mutationFn: (loginData: { user_identifier_value: number; code: string }) =>
      authService.loginAccount(loginData),
    onSuccess: () => {
      setStatus(AuthStatus.AUTHENTICATED);
      setConfirmationMethodState(null);
      queryClient.clear();
      authStep.clearAll();
    },
  });

  const checkCodeMutation = useMutation({
    mutationFn: ({ phoneNumber, code }: { phoneNumber: number; code: string }) =>
      authService.checkAccountCode({ user_identifier_value: phoneNumber, code }),
  });

  const registerMutation = useMutation({
    mutationFn: (data: RequestConfirmAccount) => authService.confirmAccount(data),
    onSuccess: () => {
      setStatus(AuthStatus.AUTHENTICATED);
      setConfirmationMethodState(null);
      queryClient.clear();
      authStep.clearAll();
    },
  });

  const initAuth = async (request: RequestInitAccountAuth): Promise<InitAccountAuthResponse> => {
    setPhoneNumber(request.user_identifier_value);
    const response = await initAuthMutation.mutateAsync(request);

    setIsUserConfirmed(response.confirmed);
    setStatus(AuthStatus.OTP_INPUT);
    if (response.code) {
      setOtpCode(response.code);
    }

    return response;
  };

  const login = async (phoneNumber: number, code: string): Promise<void> => {
    await loginMutation.mutateAsync({ user_identifier_value: phoneNumber, code });
  };

  const register = async (
    phoneNumber: number,
    code: string,
    userData: ConfirmAccountData
  ): Promise<void> => {
    await registerMutation.mutateAsync({
      user_identifier_value: phoneNumber,
      code,
      ...userData,
    });
  };

  const checkCode = async (phoneNumber: number, code: string): Promise<void> => {
    await checkCodeMutation.mutateAsync({ phoneNumber, code });
  };

  const logout = async (): Promise<void> => {
    await authService.logout();
    setPhoneNumber(null);
    setOtpCode(null);
    setIsUserConfirmed(null);
    setConfirmationMethodState(null);
    setStatus(AuthStatus.PHONE_REQUEST);
    authStep.clearAll();
    queryClient.clear();
    queueMicrotask(() => {
      void router.navigate(paths.auth.phone, { replace: true });
    });
  };

  const setConfirmationMethod = (method: AuthConfirmationMethod | null): void => {
    setConfirmationMethodState(method);
    if (method) {
      authStep.setConfirmMethod(method);
    } else {
      authStep.clearConfirmMethod();
    }
  };

  const value: AuthContextValue = {
    status,
    state: {
      phoneNumber,
      otpCode,
      isUserConfirmed,
      confirmationMethod,
    },
    requestPhonePermission,
    initAuth,
    login,
    register,
    logout,
    checkCode,
    setStatus,
    setPhoneNumber,
    setOtpCode,
    setConfirmationMethod,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
